/**
 * Janela de conversa com alguém (docs/spec/friends.md), presa embaixo, ao lado da coluna de
 * amigos. Minimizada, só o cabeçalho (com as não lidas); expandida, as mensagens e a caixa de texto.
 *
 * - Com quem não é amigo: sem presença, com a faixa "não é seu amigo" (Adicionar amigo, Bloquear).
 *   Quando não dá para mandar ("só amigos", bloqueio), o aviso fica no lugar da caixa.
 * - Enter envia; Shift+Enter quebra a linha.
 * - Ler: a conversa fica lida quando a janela está expandida e a página está à vista.
 * - Convite para uma mesa vira um cartão com "Entrar" (mesa com senha pede a senha).
 * - É uma `region`, nunca um `dialog`: o Esc da mesa (e o que mais olha os diálogos abertos) não
 *   pode confundir as janelas de conversa com um diálogo.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { translate, useLanguage, useT } from '../i18n/index.js';
import { useApp } from '../store.js';
import { FRIENDS_LIMITS, IMS_LIMITS, type ContactView, type ImsAccess, type ImsMessage, type User } from '../shared.js';
import { Icon } from '../ui/icons.js';
import { askText } from '../ui/prompt.js';
import { Avatar } from './Avatar.js';
import { chatItems, presenceDot, statusLine, type StatusTone } from './friendsModel.js';
import { useFriendsUi } from './friendsUi.js';
import { askBlock, askFriendRequest } from './dm.js';

type T = (key: string, params?: Record<string, string | number>) => string;

/** A hora de agora, de minuto em minuto (o "visto há" anda sozinho). */
export function useNow(everyMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(timer);
  }, [everyMs]);
  return now;
}

/** A linha de situação de um amigo, já traduzida. */
export function friendStatusText(contact: Pick<ContactView, 'presence' | 'lastSeenAt'>, typing: boolean, now: number, t: T): { text: string; tone: StatusTone } {
  const line = statusLine(contact, typing, now);
  if (line.seen) return { text: line.seen.key === 'friends.seen.unknown' ? t('friends.status.offlineOnly') : t(line.key, { seen: t(line.seen.key, line.seen.params) }), tone: line.tone };
  return { text: t(line.key, line.params), tone: line.tone };
}

export function FriendChatWindow({ userId, minimized, phone = false }: { userId: string; minimized: boolean; phone?: boolean }) {
  const t = useT();
  const friend = useApp((s) => s.contacts?.friends.find((c) => c.user.id === userId) ?? s.contacts?.hidden.find((c) => c.user.id === userId));
  // quem não é amigo: das "Outras conversas" ou do que veio com o histórico
  const other = useApp((s) => s.contacts?.others.find((c) => c.user.id === userId));
  const peer = useApp((s) => s.dmPeers[userId]);
  const blockedByMe = useApp((s) => !!s.contacts?.blocked.some((c) => c.user.id === userId));
  const pending = useApp((s) => !!s.contacts?.incoming.some((c) => c.user.id === userId) || !!s.contacts?.outgoing.some((c) => c.user.id === userId));
  const typing = useApp((s) => !!s.dmTyping[userId]);
  const conversation = useApp((s) => s.conversations[userId]);
  const loadConversation = useApp((s) => s.loadConversation);
  const markRead = useApp((s) => s.markConversationRead);
  const setMinimized = useFriendsUi((s) => s.setMinimized);
  const closeWindow = useFriendsUi((s) => s.closeWindow);
  const now = useNow();
  const user = friend?.user ?? other?.user ?? peer?.user;
  const unread = friend?.unreadCount ?? other?.unreadCount ?? 0;
  // o bloqueio feito agora vale na hora; o resto vem do servidor (com o histórico ou na recusa)
  const access: ImsAccess = blockedByMe ? 'blockedByMe' : (peer?.access ?? 'ok');

  useEffect(() => {
    if (!conversation?.loaded) void loadConversation(userId);
    // só ao abrir: o resto chega pelos avisos (e a reconexão recarrega)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // expandida e à vista: o que chegou fica lido
  useEffect(() => {
    if (minimized || !unread) return;
    const read = () => {
      if (!document.hidden) void markRead(userId);
    };
    read();
    document.addEventListener('visibilitychange', read);
    return () => document.removeEventListener('visibilitychange', read);
  }, [minimized, unread, userId, markRead]);

  // a janela voltou depois de recarregar a página: espera o histórico dizer quem é
  if (!user) return null;
  const name = user.displayName;
  const status = friend ? friendStatusText(friend, typing, now, t) : typing ? { text: t('friends.status.typing'), tone: 'typing' as const } : { text: t('friends.status.notFriend'), tone: 'offline' as const };
  const dot = friend ? presenceDot(friend.presence) : undefined;
  const toggle = () => setMinimized(userId, !minimized);
  const highlighted = minimized && unread > 0;

  return (
    <section className={`rd-fchat${minimized ? ' is-min' : ''}${highlighted ? ' is-unread' : ''}${phone ? ' rd-fchat--phone' : ''}`} aria-label={t('friends.chat.region', { name })}>
      <div className="rd-fchat__head">
        {phone ? (
          <button type="button" className="rd-friends__icon" aria-label={t('friends.chat.back')} onClick={() => setMinimized(userId, true)}>
            <Icon name="chevronLeft" size={22} />
          </button>
        ) : null}
        <button type="button" className="rd-fchat__who" aria-expanded={!minimized} onClick={phone ? undefined : toggle} tabIndex={phone ? -1 : 0}>
          <Avatar name={name} color={user.color} userId={userId} avatarVersion={user.avatarVersion} size={30} presence={dot} />
          <span className="rd-friends__rowText">
            <span className="rd-friends__name">{name}</span>
            <span className={`rd-friends__status is-${status.tone}`}>{status.text}</span>
          </span>
        </button>
        {highlighted ? (
          <span className="rd-count" aria-label={t('friends.unread', { n: unread })}>
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
        {phone ? null : (
          <button type="button" className="rd-friends__icon" aria-label={t(minimized ? 'friends.chat.expand' : 'friends.chat.minimize', { name })} title={t(minimized ? 'friends.chat.expand' : 'friends.chat.minimize', { name })} onClick={toggle}>
            <Icon name={minimized ? 'chevronUp' : 'minus'} size={18} />
          </button>
        )}
        <button type="button" className="rd-friends__icon" aria-label={t('friends.chat.close', { name })} title={t('friends.chat.close', { name })} onClick={() => closeWindow(userId)}>
          <Icon name="close" size={18} />
        </button>
      </div>
      {minimized ? null : (
        <>
          {friend ? null : <StrangerBar user={user} pending={pending} />}
          <ChatBody user={user} access={access} messages={conversation?.messages ?? []} loaded={!!conversation?.loaded} />
        </>
      )}
    </section>
  );
}

/** A faixa de quem não é amigo: avisa, e dá para pedir amizade ou bloquear dali mesmo. */
function StrangerBar({ user, pending }: { user: User; pending: boolean }) {
  const t = useT();
  return (
    <div className="rd-fchat__stranger">
      <span className="rd-fchat__strangerText">{pending ? t('friends.chat.pending') : t('friends.chat.stranger')}</span>
      <span className="rd-fchat__strangerActions">
        {pending ? null : (
          <button type="button" className="rd-fchat__strangerBtn" onClick={() => void askFriendRequest(user, t)}>
            {t('friends.menu.add')}
          </button>
        )}
        <button type="button" className="rd-fchat__strangerBtn is-danger" onClick={() => void askBlock(user, t)}>
          {t('friends.menu.block')}
        </button>
      </span>
    </div>
  );
}

/** No lugar da caixa, quando a mensagem não passaria: "só amigos", bloqueio meu ou dela. */
function CannotSend({ user, access }: { user: User; access: Exclude<ImsAccess, 'ok'> }) {
  const t = useT();
  const contactAction = useApp((s) => s.contactAction);
  const loadConversation = useApp((s) => s.loadConversation);
  const name = user.displayName;
  return (
    <div className="rd-fchat__cannot" role="status">
      <span>{t(`friends.chat.${access}`, { name })}</span>
      {access === 'friendsOnly' ? (
        <button type="button" className="rd-fchat__strangerBtn" onClick={() => void askFriendRequest(user, t)}>
          {t('friends.menu.add')}
        </button>
      ) : null}
      {access === 'blockedByMe' ? (
        <button
          type="button"
          className="rd-fchat__strangerBtn"
          onClick={async () => {
            await contactAction(user.id, 'unblock');
            // o que vale agora vem com o histórico
            void loadConversation(user.id);
          }}
        >
          {t('friends.blocked.unblock')}
        </button>
      ) : null}
    </div>
  );
}

function ChatBody({ user, access, messages, loaded }: { user: User; access: ImsAccess; messages: ImsMessage[]; loaded: boolean }) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const me = useApp((s) => s.user);
  const sendDirectMessage = useApp((s) => s.sendDirectMessage);
  const sendDmTyping = useApp((s) => s.sendDmTyping);
  const typing = useApp((s) => !!s.dmTyping[user.id]);
  const draft = useFriendsUi((s) => s.drafts[user.id] ?? '');
  const setDraft = useFriendsUi((s) => s.setDraft);
  const focus = useFriendsUi((s) => s.focus);
  const logRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const typingSent = useRef(0);
  const userId = user.id;
  const name = user.displayName;
  const now = Date.now();
  const items = chatItems(messages, me?.id, now);
  const time = new Intl.DateTimeFormat(language, { hour: '2-digit', minute: '2-digit' });
  const date = new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long' });

  // a última mensagem (e o "digitando…") à vista
  useLayoutEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [messages.length, typing]);

  // a janela acabou de ser pedida: o foco vai para a caixa
  useEffect(() => {
    if (focus?.userId === userId) boxRef.current?.focus();
  }, [focus, userId]);

  // parou de digitar ao fechar a janela
  useEffect(
    () => () => {
      if (typingSent.current) sendDmTyping(userId, false);
    },
    [userId, sendDmTyping],
  );

  const stopTyping = () => {
    if (!typingSent.current) return;
    typingSent.current = 0;
    sendDmTyping(userId, false);
  };
  const onChange = (text: string) => {
    setDraft(userId, text);
    if (!text.trim()) return stopTyping();
    const at = Date.now();
    if (at - typingSent.current < FRIENDS_LIMITS.typingResendMs) return;
    typingSent.current = at;
    sendDmTyping(userId, true);
  };
  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft(userId, '');
    stopTyping();
    // não foi: o texto volta para a caixa
    if (!(await sendDirectMessage(userId, text))) setDraft(userId, text);
  };

  return (
    <>
      <div ref={logRef} className="rd-fchat__log" role="log" aria-label={t('friends.chat.log', { name })}>
        {!loaded && !messages.length ? <p className="rd-fchat__empty">{t('friends.loading')}</p> : null}
        {loaded && !messages.length && access === 'ok' ? <p className="rd-fchat__empty">{t('friends.chat.first', { name })}</p> : null}
        {items.map((item) =>
          item.kind === 'day' ? (
            <div key={`d${item.at}`} className="rd-fchat__day">
              {item.day === 'today' ? t('friends.chat.today') : item.day === 'yesterday' ? t('friends.chat.yesterday') : date.format(item.at)}
            </div>
          ) : (
            <div key={item.message.id} className={`rd-fchat__msg${item.mine ? ' is-mine' : ''}${item.first ? ' is-first' : ''}${item.last ? ' is-last' : ''}`}>
              {item.message.invite ? <InviteCard message={item.message} mine={item.mine} from={name} /> : <div className="rd-fchat__bubble">{item.message.text}</div>}
              {item.last ? <span className="rd-fchat__time">{time.format(item.message.createdAt)}</span> : null}
            </div>
          ),
        )}
        {typing ? (
          <div className="rd-fchat__typing" aria-live="polite">
            {t('friends.status.typingName', { name })}
          </div>
        ) : null}
      </div>
      {access !== 'ok' ? (
        <CannotSend user={user} access={access} />
      ) : (
        <form
          className="rd-fchat__input"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <label htmlFor={`rd-fchat-${userId}`} className="rd-sr">
            {t('friends.chat.write', { name })}
          </label>
          <textarea
            id={`rd-fchat-${userId}`}
            ref={boxRef}
            rows={2}
            maxLength={IMS_LIMITS.messageMaxLength}
            placeholder={t('friends.chat.write', { name })}
            value={draft}
            onChange={(e) => onChange(e.target.value)}
            onBlur={stopTyping}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <button type="submit" className="rd-fchat__send" aria-label={t('friends.chat.send')} title={t('friends.chat.send')} disabled={!draft.trim()}>
            <Icon name="send" size={18} />
          </button>
        </form>
      )}
    </>
  );
}

/** "Fulano te chamou para a mesa X · Entrar". */
function InviteCard({ message, mine, from }: { message: ImsMessage; mine: boolean; from: string }) {
  const t = useT();
  const joinRoom = useApp((s) => s.joinRoom);
  const here = useApp((s) => s.activeRoomId === message.invite?.roomId && !!s.room);
  const invite = message.invite!;
  const enter = async () => {
    if (!invite.hasPassword) return joinRoom(invite.roomId);
    const password = await askText({
      title: t('friends.invite.passwordTitle', { room: invite.roomName }),
      label: translate(useLanguage.getState().language, 'friends.invite.password'),
      action: t('friends.invite.enter'),
      maxLength: 200,
      secret: true,
    });
    if (password) joinRoom(invite.roomId, password);
  };
  return (
    <div className="rd-fchat__invite">
      <Icon name="rooms" size={20} />
      <div className="rd-fchat__inviteText">
        <strong>{mine ? t('friends.invite.sent', { room: invite.roomName }) : t('friends.invite.received', { name: from, room: invite.roomName })}</strong>
        {invite.gone ? <span>{t('friends.invite.gone')}</span> : invite.hasPassword ? <span>{t('friends.invite.locked')}</span> : null}
      </div>
      {mine || invite.gone ? null : here ? (
        <span className="rd-fchat__inviteHere">{t('friends.invite.here')}</span>
      ) : (
        <button type="button" className="rd-btn rd-btn--action rd-friends__small" onClick={() => void enter()}>
          {t('friends.invite.enter')}
        </button>
      )}
    </div>
  );
}
